import { useSetState } from 'ahooks';
import { useEffect, useMemo, useRef } from 'react';
import { useCommunityInteraction } from '@/app/components/shared/Community';
import { useCreationNavigation, useFetch, useMediaMetrics } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ReferenceModalState } from '@/app/stores/slices/reference-modal';
import {
  CommunityEnum,
  type ExploreTab,
  type InspirationItem,
  type SelectedAsset,
  type UpdateInteractionStatusParams,
  type UpdateShareReferenceInteractionParams,
} from '@/app/types';
import { reportEvent, transformExploreDetail } from '@/app/utils';
import { apiGetDetail, apiGetShareElements } from '../apis';
import { COLLECT_EVENT_TABS_TYPE, COMMUNITY_SEARCH_OPTIONS } from '../constant';
import type { CommunitySearchOption, ViduV1ElementDetail } from '../types';

const PAGE_SIZE = 36;

export type CollectState = {
  type: CommunitySearchOption;
  page: number;
  total: number;
  items: InspirationItem[];
  deleteDialogCreation?: InspirationItem;
  openPlanDialog: boolean;
  selectedAsset?: SelectedAsset;
  hasMore: boolean;
  referenceList: ViduV1ElementDetail[];
};

export type UseCollectParams = {
  initialType?: CommunitySearchOption;
  fetchData: (params: Record<string, unknown>) => Promise<{ data?: { total?: number; list?: InspirationItem[] } }>;
  searchOptions: Record<string, string>[];
  infinityList?: boolean;
};

export type EmptyStateProps = {
  text: string;
  showCreateButton: boolean;
};

export type VoteActionType = 'vote' | 'unvote';

export const useCollectData = ({
  initialType = COMMUNITY_SEARCH_OPTIONS.ALL,
  fetchData,
  searchOptions,
  infinityList,
}: UseCollectParams) => {
  const navigationDirection = useRef<'prev' | 'next' | null>(null);

  const { openReferenceModalWithData, referenceModalUpdateReason } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    referenceModalUpdateReason: state.referenceModalUpdateReason,
  }));

  const [state, setState] = useSetState<CollectState>({
    type: initialType,
    page: 1,
    total: 0,
    items: [],
    deleteDialogCreation: undefined,
    openPlanDialog: false,
    selectedAsset: undefined,
    hasMore: false,
    referenceList: [],
  });

  const { handleEnter, handleLeave, incrementCompleteTimes } = useMediaMetrics({
    callback: () => {},
  });

  const { loading, error } = useFetch(
    () =>
      fetchData({
        'pager.page': state.page - 1,
        'pager.pagesz': PAGE_SIZE,
        types:
          state.type === COMMUNITY_SEARCH_OPTIONS.ALL
            ? []
            : state.type === 'media_asset'
              ? ['image_asset', 'media_asset', 'mini_video'] // 现在灵感对应 图片灵感和视频灵感
              : [state.type],
      }),
    {
      refreshDeps: [state.page, state.type],
      ready: state.type !== CommunityEnum.PostReferences,
      onSuccess: ({ data }) => {
        if (data) {
          const { total, list = [] } = data;
          setState({
            hasMore: (total ?? 0) > state.items.length,
            items: infinityList ? state.items.concat(list) : list,
          });
          if (list && navigationDirection.current) {
            handlePageChange(
              navigationDirection.current,
              list.map((item: InspirationItem) => ({
                ...item,
                id:
                  item.image_asset?.id ??
                  item.media_asset?.id ??
                  item.mini_video?.id ??
                  item.tutorial?.id ??
                  item.short_film?.id ??
                  '',
              })),
            );
            navigationDirection.current = null;
          }
        }
      },
    },
  );

  const { loading: shareReferenceLoading, error: shareReferenceError } = useFetch(
    () =>
      apiGetShareElements({
        'pager.page': state.page - 1,
        'pager.pagesz': PAGE_SIZE,
        'pager.page_token': '',
        is_collect: true,
      }),
    {
      refreshDeps: [state.page, state.type],
      ready: state.type === CommunityEnum.PostReferences,
      onSuccess: ({ data }) => {
        if (data) {
          const { next_page_token, share_elements } = data;
          setState({
            hasMore: !!next_page_token,
            referenceList: infinityList ? state.referenceList.concat(share_elements || []) : share_elements || [],
          });
        }
      },
    },
  );

  const { run: getDetailInfo } = useFetch(
    (id: string) =>
      apiGetDetail({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (data) {
          handleEnter();
          const { type, value } = transformExploreDetail(data) || {};
          type &&
            setState({
              selectedAsset: {
                [type]: value,
              },
            });
        }
      },
    },
  );

  const totalPages = useMemo(() => Math.ceil((state.total ?? 0) / PAGE_SIZE), [state.total]);

  const { isPrevButtonDisabled, isNextButtonDisabled, handleSelectedCreation, handleNavigation, handlePageChange } =
    useCreationNavigation({
      items: state.items.map((item) => ({
        ...item,
        id:
          item.image_asset?.id ??
          item.media_asset?.id ??
          item.mini_video?.id ??
          item.short_film?.id ??
          item.tutorial?.id,
      })),
      page: state.page,
      totalPages,
      loading,
      onPageChange: (page) => setState({ page }),
      onCreationSelect: (creation) => creation.id && getDetailInfo(creation.id),
    });

  const onNavigate = (type: 'prev' | 'next') => {
    handleLeave();
    const direction = handleNavigation(type);
    if (direction) {
      navigationDirection.current = direction;
    }
  };

  const onExpose = (index: number) => {
    reportEvent('submission_show', {
      show_position: 'my_collected_page',
    });
    if (state.type === COMMUNITY_SEARCH_OPTIONS.REFERENCE) {
      reportEvent('sharedreference_card_exposure', {
        from_page: 'profile',
        reference_id: state.referenceList[index].share?.id || '',
        // 个人主页不展示类型
        type_name: '',
      });
    }
  };

  /**
   * 灵感/短片/教程 收藏/点赞之后数据统计
   */
  const updateInteractionStatus = ({ id, obj_type, voted, collected }: UpdateInteractionStatusParams) => {
    const type = obj_type as ExploreTab;
    const updatedItems = [...state.items];

    const item = updatedItems.find((item) => item[type]?.id === id);

    if (typeof voted === 'boolean' && item?.[type]?.react_info) {
      item[type].react_info = {
        ...item[type].react_info,
        vote_count: (item[type].react_info?.vote_count ?? 0) + (voted ? 1 : -1),
        voted,
      };
    }
    if (typeof collected === 'boolean' && item?.[type]?.react_info) {
      item[type].react_info = {
        ...item[type].react_info,
        collect_count: (item[type].react_info?.collect_count ?? 0) + (collected ? 1 : -1),
        collected,
      };
    }
    setState({ items: updatedItems });
  };

  /**
   * 更新共享主体 收藏/点赞/复制之后数据统计
   * @param param0
   */
  const updateShareReferenceInteractionStatus = ({
    id,
    voted,
    count,
    collected,
  }: UpdateShareReferenceInteractionParams) => {
    const index = state.referenceList.findIndex((item) => item.share?.id === id);
    const item = state.referenceList[index];
    if (index !== -1 && item.share) {
      if (typeof voted === 'boolean' && item.share.react_info) {
        item.share.react_info = {
          ...item.share.react_info,
          vote_count: (item.share.react_info?.vote_count ?? 0) + (voted ? 1 : -1),
          voted: voted,
        };
      } else if (typeof count === 'number') {
        item.share.copy_count = count;
      } else if (typeof collected === 'boolean' && item.share.react_info) {
        item.share.react_info = {
          ...item.share.react_info,
          collect_count: (item.share.react_info?.collect_count ?? 0) + (collected ? 1 : -1),
          collected: collected,
        };
      }
      setState({
        referenceList: [...state.referenceList],
      });
    }
  };

  useEffect(() => {
    if (referenceModalUpdateReason?.type === 'like') {
      const { id, voted } = referenceModalUpdateReason;
      updateShareReferenceInteractionStatus({ id, voted });
    } else if (referenceModalUpdateReason?.type === 'copy') {
      const { id, count } = referenceModalUpdateReason;
      updateShareReferenceInteractionStatus({ id, count });
    } else if (referenceModalUpdateReason?.type === 'collect') {
      const { id, collected } = referenceModalUpdateReason;
      updateShareReferenceInteractionStatus({ id, collected });
    }
  }, [referenceModalUpdateReason]);

  const { vote, unVote } = useCommunityInteraction({
    onInteractionStatusChange: ({ id, obj_type, voted }) => {
      updateInteractionStatus({ id, obj_type: obj_type as CommunityEnum, voted });
    },
  });

  const { collect, unCollect } = useCommunityInteraction({
    onInteractionStatusChange: updateShareReferenceInteractionStatus,
  });

  const handleUpdateCollect = (params: UpdateInteractionStatusParams) => {
    if (state.type !== COMMUNITY_SEARCH_OPTIONS.REFERENCE && state.type !== COMMUNITY_SEARCH_OPTIONS.ALL) {
      reportEvent(COLLECT_EVENT_TABS_TYPE[state.type], {
        interaction_position: 'my_collected_page',
        interaction_type: 'star',
        content_type: params.obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
      });
    }
    return collect(params);
  };

  const handleUpdateUnCollect = (params: UpdateInteractionStatusParams) => {
    if (state.type !== COMMUNITY_SEARCH_OPTIONS.REFERENCE && state.type !== COMMUNITY_SEARCH_OPTIONS.ALL) {
      reportEvent(COLLECT_EVENT_TABS_TYPE[state.type], {
        interaction_position: 'my_collected_page',
        interaction_type: 'cancel_star',
        content_type: params.obj_type === CommunityEnum.ImageAsset ? 'image' : 'video',
      });
    }
    return unCollect(params);
  };

  const handleReferenceDetail = (id: string, from?: ReferenceModalState['referenceModalFrom']) => {
    openReferenceModalWithData({
      referenceModalOpen: true,
      referenceModalMode: 'view',
      referenceModalFrom: from || 'profile',
      referenceModalFromPage: 'profile',
      element: {
        id: id,
        modality: 'image',
      },
    });
  };

  return {
    state,
    setState,
    loading: state.type === CommunityEnum.PostReferences ? shareReferenceLoading : loading,
    error: state.type === CommunityEnum.PostReferences ? shareReferenceError : error,
    searchOptions,
    isPrevButtonDisabled,
    isNextButtonDisabled,
    onNavigate,
    handleSelectedCreation,
    vote,
    unVote,
    getDetailInfo,
    incrementCompleteTimes,
    handleLeave,
    handleEnter,
    handleReferenceDetail,
    collect: handleUpdateCollect,
    unCollect: handleUpdateUnCollect,
    onExpose,
  };
};
